import {
    Asset,
    Brand,
    CompositeProduct,
    FlavorNote,
    FoodPairing,
    Ingredient,
    OtherProductTraits,
    ProductTraits,
    ServingTemperature,
    ServingVessel,
    Style,
} from 'src/types/schema/graphql'

import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import { immer } from 'zustand/middleware/immer'
import { parseEnumLabel } from 'src/util/helpers'

interface IuseBrewDetailStore {
    productData: CompositeProduct
    setProductData: (productData: CompositeProduct) => void
    __typename: string
    id: string
    abv?: number
    srm?: number
    ibu?: number
    assets?: Asset[]
    brand?: Brand
    color?: string
    createdBy?: string
    createdDate?: string
    description?: string
    foodPairings?: string
    foodPairingList?: FoodPairing[]
    guid?: string
    hintsOfFlavorNotes?: FlavorNote[]
    ingredients?: Ingredient[]
    isVerified?: boolean
    isNewProduct?: boolean
    lastUpdatedBy?: string
    primaryFlavorNotes?: FlavorNote[]
    releaseDate?: string
    servingTemperature?: ServingTemperature
    servingVessels?: ServingVessel[]
    slug?: string
    tastingExperience?: string
    title: string
    traits?: ProductTraits
    updatedDate?: string
    style: Style
    perceivedBitterness?: number
    isBrewLoading: boolean
    clearBrewModalStore: () => void
    sourness?: string
    appearance?: string
    acidity?: string
    probiotics?: string
    perceivedSweetness?: string
    teaCharacter?: string
    brixCount?: number
    carbonation?: string
    body?: string
    tannin?: string
    otherTraits?: OtherProductTraits[]
}

const defaultData = {
    __typename: '',
    id: '',
    abv: 0,
    srm: null,
    ibu: null,
    assets: [] as Asset[],
    brand: null,
    color: '',
    createdBy: '',
    createdDate: '',
    description: '',
    foodPairings: '',
    foodPairingList: null,
    guid: '',
    hintsOfFlavorNotes: [] as FlavorNote[],
    ingredients: [] as Ingredient[],
    isVerified: false,
    isNewProduct: false,
    lastUpdatedBy: '',
    locationsAvailable: [] as Location[],
    primaryFlavorNotes: [] as FlavorNote[],
    releaseDate: '',
    servingTemperature: null,
    servingVessels: [] as ServingVessel[],
    slug: '',
    tastingExperience: '',
    title: '',
    traits: null,
    updatedDate: '',
    style: null,
    perceivedBitterness: null,
    sourness: null,
    appearance: null,
    acidity: null,
    probiotics: null,
    perceivedSweetness: null,
    teaCharacter: null,
    brixCount: null,
    carbonation: null,
    body: null,
    tannin: null,
    otherTraits: null,
}

const useBrewDetailStore = create<IuseBrewDetailStore>()(
    devtools(
        immer((set) => ({
            ...defaultData,
            productData: null,
            isBrewLoading: false,
            clearBrewModalStore: () => set(defaultData),
            setProductData: (productData) => {
                set({
                    productData,
                    __typename: productData?.__typename,
                    id: productData?.id,
                    abv: productData?.abv,
                    ibu: productData?.ibu,
                    srm: productData?.srm,
                    assets:
                        (productData?.assets?.length > 0
                            ? productData?.assets
                            : productData?.brand?.assets) || null,
                    brand: productData?.brand,
                    color: productData?.color,
                    createdBy: productData?.createdBy,
                    createdDate: productData?.createdDate,
                    description: productData?.description,
                    foodPairingList: productData?.foodPairingList,
                    guid: productData?.guid,
                    hintsOfFlavorNotes: productData?.hintsOfFlavorNotes,
                    ingredients: productData?.ingredients,
                    isVerified: productData?.isVerified,
                    isNewProduct: productData?.isNewProduct,
                    lastUpdatedBy: productData?.lastUpdatedBy,
                    primaryFlavorNotes: productData?.primaryFlavorNotes,
                    releaseDate: productData?.releaseDate,
                    servingTemperature: productData?.servingTemperature,
                    servingVessels: productData?.servingVessels,
                    slug: productData?.slug,
                    tastingExperience: productData?.tastingExperience,
                    title: productData?.title,
                    traits: productData?.traits,
                    updatedDate: productData?.updatedDate,
                    style: productData?.style,
                    perceivedBitterness: productData?.perceivedBitterness,
                    sourness: productData?.sourness?.toString(),
                    appearance: productData?.appearance?.toString(),
                    acidity: productData?.acidity?.toString(),
                    probiotics: productData?.probiotics?.toString(),
                    perceivedSweetness: productData?.perceivedSweetness
                        ? parseEnumLabel(productData?.perceivedSweetness?.toString())
                        : null,
                    teaCharacter: productData?.teaCharacter?.toString(),
                    brixCount: productData?.brixCount,
                    carbonation: productData?.carbonation?.toString(),
                    body: productData?.body?.toString(),
                    tannin: productData?.tannin?.toString(),
                    otherTraits: productData?.traits?.otherTraits,
                })
            },
        })),
        { name: `useBrewDetailStore` }
    )
)

export { useBrewDetailStore }
